/* eslint-disable no-unused-vars */
import React, { Fragment, useEffect, useState, useMemo } from "react"; // Added useMemo
import TopBarProgress from "react-topbar-progress-indicator";
import { getAllRoutes, updateRole, sendPermissions, getAllTypes, getAllRolesWithPermissions, getSingleRolePermissions } from "../../../services/routes";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import styled from '@emotion/styled';
import { Shield, Lock, Users, Check, X, ChevronDown, ChevronUp, Key, Settings, UserCheck, AlertCircle, CheckCircle2, Save, Search, Activity, Database, Layers, Menu, BookOpen, MousePointerClick } from "lucide-react"; // Added Menu, BookOpen, MousePointerClick
import { navbar } from "./navbarConfig"; // Adjust path as needed
import CreateRoleButton from "../../../components/CreateRoleButton";

// Define the route that must always be selected
const FIXED_ROUTE_URL = "/admin/home";

// ... (ThemedButton and styled components remain unchanged) ...
const ThemedButton = styled(Button)(({ theme, loading }) => ({
  backgroundColor: "#000",
  // position: "fixed",
  // bottom: "2%",
  // left: "50%",
  color: "#fff",
  borderColor: "#000",
  borderRadius: "1rem",
  padding: "1rem 2rem",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  transition: "all 300ms ease",
  transform: "scale(1)",
  "&:hover": {
    backgroundColor: "#000",
    borderColor: "#000",
    transform: "scale(1.05)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    // position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#fff",
    opacity: 0,
    transition: "opacity 300ms ease",
  },
  "&:hover::after": {
    opacity: 0.2,
  },
}));


const Permissions = () => {
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [allRoutes, setAllRoutes] = useState([]);
  const [personName, setPersonName] = React.useState([]);
  const [backendTitles, setBackendTitles] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [dataToSend, setDataToSend] = useState({});
  const [userRole, setUserRole] = useState({});
  const [selectedRoutesIds, setSelectedRoutesIds] = useState([]);
  const [backendRoute, setBackendRoute] = useState("");
  const [backendArr, setBackendArr] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createRole, setCreateRole] = useState(false);

  // Helper function to flatten the navbar links for quick lookup
  const getNavbarLinks = (navItems) => {
    let links = new Set();
    const traverse = (items) => {
      items?.forEach(item => {
        if (item.link && item.link.startsWith('/admin/')) {
          links.add(item.link);
        }
        if (item.children && item.children.length > 0) {
          traverse(item.children);
        }
      });
    };
    traverse(navItems);
    return links;
  };

  const navbarLinks = useMemo(() => getNavbarLinks(navbar), []);

  // --- Filtering Logic using useMemo remains unchanged ---
  const { navbarRoutes, otherRoutes } = useMemo(() => {
    const navRoutes = [];
    const others = [];

    // Apply the initial text search filter first
    const searchFilteredRoutes = allRoutes.filter(route =>
      route.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    searchFilteredRoutes.forEach(route => {
      // Filter based on whether the route's frontend URL exists in the flattened navbar links
      if (route.frontend && navbarLinks.has(route.frontend)) {
        navRoutes.push(route);
      } else {
        // This includes parent categories with null links, and other non-menu routes.
        others.push(route);
      }
    });


    return { navbarRoutes: navRoutes, otherRoutes: others };
  }, [allRoutes, searchTerm, navbarLinks]);
  // --- End of Filtering Logic ---


  // ... (fetchRoutes, fetchTypes, useEffect, handleCategory, getStringsArr, handleBackendRouteChange remain unchanged) ...
  const fetchRoutes = async () => {
    setLoading(true);
    try {
      // const reee = await getAllRoutes();
      const res = await getAllRolesWithPermissions();
      // console.log("res.data",res?.data)
      setAllRoutes(res?.data || []);
      // Auto-expand all categories initially
      const expanded = {};
      res.data?.data?.forEach(route => {
        expanded[route._id] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to fetch routes', { containerId: "permission" });
    }
    setLoading(false);
  };

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await getAllTypes();
      const filteredUserTypes = res?.data?.data?.filter(user => user.type !== 'admin') || [];
      setUserTypes(filteredUserTypes);

    } catch (error) {
      toast.error('Failed to fetch user types', { containerId: "permission" });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTypes();
    fetchRoutes();
    if(createRole){
    setCreateRole(false)
    }
  }, [createRole ]);
  // ... (handleCategory, getStringsArr remain unchanged) ...
  const handleCategory = (e) => {
    console.log(e)
    setBackendArr(e.backend);
    setSelectedCategory(e?.label);
    setPersonName([]);
  };

  const getStringsArr = (arr) => {
    return arr?.map((item) => item?.title);
  };

  // NEW HELPER: Get the count of unique backend route IDs
  const getUniqueBackendIdCount = (category) => {
      if (!category.backend) return 0;
      const uniqueIds = new Set(category.backend.map(route => route._id));
      return uniqueIds.size;
  };

  /**
   * NEW HELPER FUNCTION: Recursively finds all frontend URLs in a linked chain. (Kept for selectAllInCategory and handleSubmit)
   * @param {string} startUrl - The frontend URL to start from.
   * @returns {string[]} An array of unique frontend URLs in the chain (including the startUrl).
   */
  const getCompleteRouteChainUrls = (startUrl) => {
    const queue = [startUrl];
    const chainUrls = new Set([startUrl]);

    while (queue.length > 0) {
      const currentUrl = queue.shift();
      const currentCategory = allRoutes.find(r => r.frontend === currentUrl);

      if (currentCategory && currentCategory.linked) {
        currentCategory.linked.forEach(linkedUrl => {
          if (!chainUrls.has(linkedUrl)) {
            chainUrls.add(linkedUrl);
            queue.push(linkedUrl); // Add to queue to process its links
          }
        });
      }
    }

    return Array.from(chainUrls);
  };

// UPDATED handleSelectType function (Handles initial loading of permissions from API - NO CHAIN LOADING)
  const handleSelectType = async (param) => {
    // console.log("param",param)
    // Clear previously selected permissions
    setDataToSend({});

    let selectedRoles = {};
    if (userRole[param._id]) {
      delete selectedRoles[param._id];
      setUserRole(selectedRoles);
    } else {
      selectedRoles[param._id] = param
      setUserRole(selectedRoles);

      // Fetch and set permissions for the newly selected role
      try {
        setLoading(true);
        const permissionsRes = await getSingleRolePermissions(param?._id);
        const permissions = permissionsRes?.data?.permissions || [];
        const newSelectedPermissions = {};

        // Helper function to process a route category and select all its backend routes
        const processCategoryForSelection = (category, dataObject) => {
            const categoryId = category._id;
            if (!dataObject[categoryId]) {
                dataObject[categoryId] = {};
            }
            // Automatically select ALL backend routes for this category
            category.backend?.forEach(apiRoute => {
                // Duplicate IDs will be overwritten here, resulting in only unique keys in dataObject[categoryId]
                dataObject[categoryId][apiRoute._id] = { title: apiRoute.title, url: apiRoute.url };
            });
        };

        // FIX: Iterate ONLY over primary permissions returned by the API. 
        // We do NOT use the linked chain logic here, addressing the issue of unselected routes being pulled in.
        permissions?.forEach(permissionCategory => {
          // 1. Find the full category object (from allRoutes)
          const fullCategory = allRoutes.find(r => r.title === permissionCategory.title);
          
          if (fullCategory) {
             // Process only the category explicitly returned by the API.
             processCategoryForSelection(fullCategory, newSelectedPermissions);
          }
        });
        
        // **********************************************
        // 1. FORCE SELECTION OF FIXED_ROUTE_URL ON LOAD
        // **********************************************
        const fixedRoute = allRoutes.find(r => r.frontend === FIXED_ROUTE_URL);

        if (fixedRoute) {
            processCategoryForSelection(fixedRoute, newSelectedPermissions);
            toast.info(`'/admin/home' is mandatory and automatically selected.`, { containerId: "permission" });
        }


        // console.log("newSelectedPermissions", newSelectedPermissions)
        setDataToSend(newSelectedPermissions);
        toast.success(`Permissions for ${param.type} loaded!`, { containerId: "permission" });
      } catch (error) {
        console.error("Error fetching permissions for role:", error);
        toast.error('Failed to fetch permissions for this role.', { containerId: "permission" });
      } finally {
        setLoading(false);
      }
    }
  };

  // UPDATED selectAllInCategory (Handles user selecting/deselecting a category and its chain - Logic remains as chain to ensure consistency)
  const selectAllInCategory = (category) => {
    // 1. Get the frontend URL of the category being toggled
    const primaryFrontendUrl = category.frontend;
    
    // **********************************************
    // 2. CHECK IF THIS IS THE FIXED ROUTE AND RESTRICT
    // **********************************************
    if (primaryFrontendUrl === FIXED_ROUTE_URL) {
        toast.error(`Permissions for '/admin/home' are mandatory and cannot be changed.`, { containerId: "permission" });
        return;
    }


    // FIX: Get unique ID count for comparison
    const uniqueBackendLen = getUniqueBackendIdCount(category);

    if (!primaryFrontendUrl) {
        // Handle categories without a frontend link (e.g., parent/group categories)
        // by just toggling that category itself.
        // FIX: Use unique ID count for comparison
        const isSelected = dataToSend[category._id] && Object.keys(dataToSend[category._id]).length === uniqueBackendLen;
        let newDataToSend = { ...dataToSend };

        if (isSelected) {
            delete newDataToSend[category._id];
            setDataToSend(newDataToSend);
            toast.info(`Permissions for ${category.title} DEACTIVATED.`, { containerId: "permission" });
        } else {
            newDataToSend[category._id] = {};
            category.backend?.forEach(route => {
                // Duplicate IDs will overwrite, which is correct for unique key storage
                newDataToSend[category._id][route._id] = { ...route }; 
            });
            setDataToSend(newDataToSend);
            toast.success(`Permissions for ${category.title} ACTIVATED.`, { containerId: "permission" });
        }
        return;
    }


    // 2. Identify all routes in the chain (A, B, C...)
    const chainUrls = getCompleteRouteChainUrls(primaryFrontendUrl);
    
    // 3. Determine the action (Select or Deselect)
    // Check if the primary category is currently fully selected
    // FIX: Use unique ID count for comparison
    const isPrimarySelected = dataToSend[category._id] && Object.keys(dataToSend[category._id]).length === uniqueBackendLen;
    let newDataToSend = { ...dataToSend };

    if (isPrimarySelected) {
        // DESELECT ACTION: Delete the primary category AND all categories in the chain
        chainUrls.forEach(url => {
            const chainCategory = allRoutes.find(r => r.frontend === url);
            if (chainCategory) {
                delete newDataToSend[chainCategory._id];
            }
        });
        setDataToSend(newDataToSend);
        toast.info(`Permissions for ${category.title} and its linked chain DEACTIVATED.`, { containerId: "permission" });

    } else {
        // SELECT ACTION: Select the primary category AND all categories in the chain
        chainUrls.forEach(url => {
            const chainCategory = allRoutes.find(r => r.frontend === url);
            if (chainCategory) {
                // Helper to process selection (simplified version)
                const categoryId = chainCategory._id;
                newDataToSend[categoryId] = {};
                chainCategory.backend?.forEach(route => {
                    // Duplicate IDs will overwrite, which is correct for unique key storage
                    newDataToSend[categoryId][route._id] = { title: route.title, url: route.url };
                });
            }
        });
        setDataToSend(newDataToSend);
        toast.success(`Permissions for ${category.title} and its linked chain ACTIVATED.`, { containerId: "permission" });
    }
  };


  // REVERTED handleSubmit to the simpler logic. The dataToSend object already contains all 
  // linked permissions added by selectAllInCategory.
  const handleSubmit = async () => {
    setIsSubmitting(true);
    let permissions = []
    
    // We iterate over the categories in dataToSend, which already contains the full, consistent 
    // set of selected categories, including all linked routes.
    Object.entries(dataToSend).forEach(([categoryId, val]) => {
      // Find the category from the global list
      let category = allRoutes.find(elem => elem._id === categoryId);
      
      if (category) {
        permissions.push({
          title: category.title,
          frontendUrl: category.frontend,
          apiRoutes: Object.keys(val).map(apiId => {
            return {
              title: dataToSend[categoryId][apiId].title,
              url: dataToSend[categoryId][apiId].url
            };
          })
        });
      }
    });


    if (Object.keys(userRole).length > 0 && permissions.length > 0) {
      try {
        // Updated to use the new updateRole function
        await updateRole(userRole[Object.keys(userRole)[0]]._id, {
          type: userRole[Object.keys(userRole)[0]].type,
          permissions: permissions,
        });
        toast.success('Permissions updated successfully!', { containerId: "permission" });
        // Reset selections after successful submission
        setTimeout(() => {
          setDataToSend({});
          setUserRole({});
        }, 1500);
      } catch (error) {
        toast.error('Failed to update permissions', { containerId: "permission" });
      }
    } else {
      toast.error('Please select at least one role and permission', { containerId: "permission" });
    }
    setIsSubmitting(false);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getPermissionCount = (categoryId) => {
    return dataToSend[categoryId] ? Object.keys(dataToSend[categoryId]).length : 0;
  };

  const getTotalPermissions = (category) => {
    return category.backend?.length || 0;
  };
  
  // --- New component for rendering permission cards remains unchanged ---
  const PermissionCategorySection = ({ title, routes, icon: Icon, defaultExpanded = true }) => {
    const [isSectionExpanded, setIsSectionExpanded] = useState(defaultExpanded);
    if (routes.length === 0) return null;

    return (
      <div style={{ marginBottom: '32px' }}>
        <button
          onClick={() => setIsSectionExpanded(prev => !prev)}
          style={{
            width: '100%',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            marginBottom: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon style={{ width: '24px', height: '24px', color: '#000' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              {title} ({routes.length})
            </h2>
          </div>
          {isSectionExpanded ? (
            <ChevronUp style={{ width: '20px', height: '20px', color: '#9ca3af' }} />
          ) : (
            <ChevronDown style={{ width: '20px', height: '20px', color: '#9ca3af' }} />
          )}
        </button>

        {isSectionExpanded && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              width: '100%',
            }}
          >
            {routes.map((category) => {
              // FIX: Define a consistent selection state that matches the toggle logic's requirements (using unique IDs).
              const uniqueBackendLenForUI = getUniqueBackendIdCount(category);
              const isCategorySelected = dataToSend[category._id] && Object.keys(dataToSend[category._id]).length === uniqueBackendLenForUI;
              
              // **********************************************
              // UI LOGIC FOR FIXED ROUTE
              // **********************************************
              const isFixedRoute = category.frontend === FIXED_ROUTE_URL;
              const effectiveIsSelected = isCategorySelected || isFixedRoute;

              return (
              <div
                key={category._id}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  transform: 'translateY(0)',
                  height: "fit-content",
                  // Use consistent check for border
                  border: effectiveIsSelected ? '2px solid #000' : '2px solid transparent', // Highlight selected
                  opacity: isFixedRoute ? 0.8 : 1, // Visual hint that it's fixed
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* MODIFIED: The main card area is now a DIV for selection only */}
                <div
                  // On click, only toggle the permission state (select all/deselect all)
                  onClick={() => {
                    if (!isFixedRoute) {
                        selectAllInCategory(category); // <<< ONLY SELECTION LOGIC
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    // Use consistent check for background
                    background: effectiveIsSelected ? 'linear-gradient(135deg, rgba(182, 188, 181, 0.1) 0%, rgba(182, 188, 181, 0.1) 100%)' : 'transparent',
                    border: 'none',
                    cursor: isFixedRoute ? 'not-allowed' : 'pointer', // Disable cursor for fixed route
                    transition: 'background-color 0.2s ease',
                  }}
                  // Move hover styles from the old button to this new div
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = effectiveIsSelected ? 'linear-gradient(135deg, rgba(182, 188, 181, 0.2) 0%, rgba(182, 188, 181, 0.2) 100%)' : '#f9fafb')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = effectiveIsSelected ? 'linear-gradient(135deg, rgba(182, 188, 181, 0.1) 0%, rgba(182, 188, 181, 0.1) 100%)' : 'transparent')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        padding: '6px',
                        // Use consistent check for checkmark background
                        background: effectiveIsSelected ? '#000' : '#d1d5db',
                        borderRadius: '6px',
                        transition: 'background-color 0.3s ease'
                      }}
                    >
                      {/* Show Lock icon for fixed route, Check otherwise */}
                      {isFixedRoute ? (
                          <Lock style={{ width: '16px', height: '16px', color: 'white' }} />
                      ) : (
                          <Check style={{ width: '16px', height: '16px', color: 'white' }} />
                      )}
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <h3
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          // Use consistent check for title color
                          color: effectiveIsSelected ? '#000' : '#1f2937',
                          margin: 0,
                          lineHeight: 1.3,
                        }}
                      >
                        {category.title}
                      </h3>
                      <p
                        style={{
                          fontSize: '12px',
                          // Use consistent check for description color
                          color: effectiveIsSelected ? '#000' : '#6b7280',
                          marginTop: '2px',
                          lineHeight: 1.3,
                          // Use consistent check for description font weight
                          fontWeight: effectiveIsSelected ? 'bold' : 'normal'
                        }}
                      >
                        {/* Use custom description for fixed route */}
                        {isFixedRoute ? 'MANDATORY (Cannot be changed)' : (isCategorySelected ? 'ACTIVE (Click to Deactivate)' : 'INACTIVE (Click to Activate)')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Container for API count and the new toggle button */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* API Count */}
                    {effectiveIsSelected && (
                      <div
                        style={{
                          padding: '2px 6px',
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          color: '#000',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: '700',
                          width: "max-content"
                        }}
                      >
                        {getPermissionCount(category._id)} APIs
                      </div>
                    )}

                    {/* NEW: Dedicated button for Expansion/Collapse */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Crucial: Prevents the parent DIV (selection logic) from firing
                        toggleCategory(category._id);
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        padding: '4px', // Increase clickable area
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s ease',
                      }}
                    >
                      {expandedCategories[category._id] ? (
                        <ChevronUp style={{ width: '16px', height: '16px', color: '#000' }} /> // Use a darker color to highlight the interaction
                      ) : (
                        <ChevronDown style={{ width: '16px', height: '16px', color: '#000' }} /> // Use a darker color to highlight the interaction
                      )}
                    </button>
                  </div>
                </div>
                {/* End of main selection DIV */}

                {expandedCategories[category._id] && (
                  <div style={{ padding: '0 16px 16px 16px', borderTop: '1px solid #f3f4f6' }}>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '12px 0 8px 0', fontWeight: '600' }}>
                      Associated Backend Routes (Read Only):
                    </p>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                        gap: '8px',
                      }}
                    >
                      {/* REMOVED: Individual backend route buttons, as they conflict with the "select all by category" requirement */}
                      {category.backend?.map((item) => (
                        <div
                          key={item?._id}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            // The individual backend route check is fine, as it's implied by a full selection
                            background: dataToSend[category._id]?.[item._id]
                              ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)'
                              : '#f9fafb',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            lineHeight: 1.3,
                            color: '#374151',
                          }}
                        >
                          {item.title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )})}
          </div>
        )}
      </div>
    );
  };
  // --- End of new component ---


  return (
    <div className="container-scroller" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {loading && <TopBarProgress />}
      <ToastContainer
      stacked 
        enableMultiContainer
        containerId={"permission"}
        position="top-right"
      />

      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header Section remains unchanged */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          padding: '32px',
          marginBottom: '32px',
          transform: 'translateY(0)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #000 0%, #000 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Shield style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>
              <div>
                <h1 style={{
                  fontSize: '32px',
                  fontWeight: 'bold',
                  // background: 'linear-gradient(135deg, #000 0%, #000 100%)',
                  // WebkitBackgroundClip: 'text',
                  // WebkitTextFillColor: 'transparent',
                  margin: 0
                }}>
                  Permission Management
                </h1>
                <p style={{ color: '#6b7280', marginTop: '4px' }}>
                  Manage user roles and access controls with confidence
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9ca3af', fontSize: '14px' }}>
              <Lock style={{ width: '16px', height: '16px' }} />
              <span>Secure Access Control</span>
            </div>
          </div>
        </div>

        {/* Stats Cards remain unchanged */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderLeft: '4px solid #000'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Total Roles</p>
                <h3 style={{ fontSize: '28px', fontWeight: 'bold', color: '#000', margin: '8px 0 0 0' }}>
                  {userTypes.length}
                </h3>
              </div>
              <Users style={{ width: '40px', height: '40px', color: '#000', opacity: 0.3 }} />
            </div>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderLeft: '4px solid #000'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Categories</p>
                <h3 style={{ fontSize: '28px', fontWeight: 'bold', color: '#000', margin: '8px 0 0 0' }}>
                  {allRoutes.length}
                </h3>
              </div>
              <Layers style={{ width: '40px', height: '40px', color: '#000', opacity: 0.3 }} />
            </div>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderLeft: '4px solid #000'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Active Permissions</p>
                <h3 style={{ fontSize: '28px', fontWeight: 'bold', color: '#000', margin: '8px 0 0 0' }}>
                  {/* {Object.values(dataToSend).reduce((acc, cat) => acc + Object.keys(cat).length, 0)} */}
                  {Object.keys(dataToSend)?.length}
                </h3>
              </div>
              <Activity style={{ width: '40px', height: '40px', color: '#000', opacity: 0.3 }} />
            </div>
          </div>
        </div>

        {/* Role Selection Section remains unchanged */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          padding: '24px',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", gap: '8px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px'}}>
            <Users style={{ width: '20px', height: '20px', color: '#000' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              Step 1: Select User Role
            </h2>
            </div>
             <div>
              <CreateRoleButton setCreateRole={setCreateRole}/>
             </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {userTypes?.map((userType) => (
              <button
                key={userType?._id}
                onClick={() => handleSelectType(userType)}
                onMouseEnter={() => setHoveredCard(userType?._id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  position: 'relative',
                  padding: '16px',
                  borderRadius: '12px',
                  border: userRole[userType._id] ? '2px solid #000' : '2px solid #e5e7eb',
                  background: userRole[userType._id]
                    ? 'linear-gradient(135deg, rgba(182, 188, 181, 0.1) 0%, rgba(182, 188, 181, 0.1) 100%)'
                    : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: userRole[userType._id] ? 'scale(1.05)' : hoveredCard === userType._id ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: userRole[userType._id]
                    ? '0 8px 20px rgba(182, 188, 181, 0.1)'
                    : hoveredCard === userType._id
                      ? '0 4px 15px rgba(0,0,0,0.1)'
                      : '0 2px 10px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      padding: '8px',
                      borderRadius: '8px',
                      backgroundColor: userRole[userType._id] ? '#000' : '#d1d5db',
                      transition: 'background-color 0.3s ease'
                    }}>
                      <UserCheck style={{ width: '20px', height: '20px', color: 'white' }} />
                    </div>
                    <span style={{
                      fontWeight: '500',
                      color: userRole[userType._id] ? '#000' : '#374151',
                      textTransform: "capitalize",
                    }}>
                      {userType?.type}
                    </span>
                  </div>
                  {userRole[userType?._id] && (
                    <Check style={{
                      width: '20px',
                      height: '20px',
                      color: '#000',
                      animation: 'bounce 0.5s ease'
                    }} />
                  )}
                </div>
                {hoveredCard === userType._id && !userRole[userType._id] && (
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#000',
                    color: 'white',
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: '999px',
                    animation: 'pulse 1.5s infinite'
                  }}>
                    Click to select
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Search Bar remains unchanged */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          padding: '16px',
          marginBottom: '32px'
        }}>
          <div style={{ position: 'relative' }}>
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              width: '20px',
              height: '20px'
            }} />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '8px',
                paddingBottom: '8px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                outline: 'none',
                transition: 'all 0.3s ease',
                fontSize: '14px'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#000';
                e.target.style.boxShadow = '0 0 0 3px rgba(182, 188, 181, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Permissions Categories: NOW SPLIT */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <Key style={{ width: '20px', height: '20px', color: '#000' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              Step 2: Configure Permissions (Frontend Route Based)
            </h2>
          </div>

          {/* 1. Navbar Permissions Section */}
          <PermissionCategorySection
            title="Navbar Permissions"
            routes={navbarRoutes}
            icon={Menu}
            defaultExpanded={true}
          />

          {/* 2. Other Permissions Section */}
          <PermissionCategorySection
            title="Other Permissions"
            routes={otherRoutes}
            icon={BookOpen}
            defaultExpanded={true}
          />

        </div>
        {/* End of Permissions Categories: NOW SPLIT */}

        {/* Submit Button and Summary remain unchanged */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', marginBottom: "40px" }}>
          {/* Summary Card */}
          {(Object.keys(dataToSend).length > 0 || Object.keys(userRole).length > 0) && (
            <div style={{
              width: '100%',
              maxWidth: '600px',
              background: 'linear-gradient(135deg, rgba(182, 188, 181, 0.1) 0%, rgba(182, 188, 181, 0.1) 100%)',
              borderRadius: '16px',
              padding: '24px',
              border: '2px solid #000'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                Summary
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.keys(userRole).length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserCheck style={{ width: '16px', height: '16px', color: '#000' }} />
                    <span style={{ color: '#374151' }}>
                      Role: <strong>{userRole[Object.keys(userRole)[0]]?.type}</strong>
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Shield style={{ width: '16px', height: '16px', color: '#000' }} />
                  <span style={{ color: '#374151' }}>
                    Total Permissions Selected: <strong>
                      {Object.values(dataToSend).reduce((acc, cat) => acc + Object.keys(cat).length, 0)}
                    </strong>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Layers style={{ width: '16px', height: '16px', color: '#000' }} />
                  <span style={{ color: '#374151' }}>
                    Categories Modified: <strong>{Object.keys(dataToSend).length}</strong>
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-center">
            <ThemedButton
              onClick={handleSubmit}
              disabled={loading}
              startIcon={<SaveIcon />}
            >
              Save Permissions
            </ThemedButton>
          </div>

        </div>
      </div>
    </div>
  )
}


export default Permissions;